// =============================================================================
// billing.js, small client for the Phase 32 Billing endpoints.
//
// Endpoints used:
//   GET  /api/billing/me           , current subscription + trial state
//   POST /api/billing/trial/start  , kick off a 14-day no-card trial
//   POST /api/billing/checkout     , create Razorpay subscription (INR or USD)
//   POST /api/billing/portal       , placeholder (501 until providers wired)
//   POST /api/billing/verify-signature, Phase 37.2: verify modal-success HMAC
//
// Phase 37.2 additions:
//   • `loadRazorpayCheckout()`, lazy-loads Razorpay's checkout.js SDK once
//   • `openRazorpayModal()`   , opens the embedded modal with subscription_id
//
// All calls degrade silently, billing UI must never crash the rest of the
// app. Callers should treat a null/undefined response as "unknown state".
// =============================================================================
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_SDK_URL = 'https://checkout.razorpay.com/v1/checkout.js';

/** GET /api/billing/me, returns the full billing snapshot. */
export async function getBillingMe(token) {
  if (!token) return null;
  try {
    const r = await axios.get(`${BACKEND_URL}/api/billing/me`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 8000,
    });
    return r.data;
  } catch (e) {
    console.warn('getBillingMe failed:', e?.response?.data?.detail || e?.message);
    return null;
  }
}

/**
 * POST /api/billing/trial/start
 *
 * @param {string} token  Supabase access token
 * @param {'horizon'|'summit'} plan
 * @returns {Promise<{ ok:true, trial_plan, trial_ends_at, days_left } | { error:string }>}
 */
export async function startTrial(token, plan) {
  if (!token) return { error: 'Sign in to start your free trial.' };
  try {
    const r = await axios.post(
      `${BACKEND_URL}/api/billing/trial/start`,
      { plan },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
    );
    return r.data;
  } catch (e) {
    const detail = e?.response?.data?.detail || e?.message || 'Could not start your trial right now.';
    return { error: detail };
  }
}

/**
 * POST /api/billing/trial/skip
 *
 * Decline the auto-granted Horizon trial; go straight to Free forever.
 * One-way action (trial_used remains true).
 */
export async function skipTrial(token) {
  if (!token) return { error: 'Sign in first.' };
  try {
    const r = await axios.post(
      `${BACKEND_URL}/api/billing/trial/skip`,
      {},
      { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 },
    );
    return r.data;
  } catch (e) {
    const detail = e?.response?.data?.detail || e?.message || 'Could not switch to Free right now.';
    return { error: detail };
  }
}

/**
 * POST /api/billing/checkout, creates a Razorpay subscription.
 *
 * Phase 37.2 response shape:
 *   {
 *     ok, provider, subscription_id, key_id, status, currency,
 *     amount_cents, plan, cycle,
 *     short_url,           // fallback redirect
 *     url,                 // alias of short_url (back-compat)
 *     prefill: { name, email, contact },
 *     notes,
 *   }
 */
export async function startCheckout(token, plan, cycle) {
  if (!token) return { error: 'Sign in to subscribe.' };
  try {
    const r = await axios.post(
      `${BACKEND_URL}/api/billing/checkout`,
      { plan, cycle },
      { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
    );
    return r.data;
  } catch (e) {
    const detail = e?.response?.data?.detail || e?.message || 'Checkout is not available right now.';
    return { error: detail, code: e?.response?.status };
  }
}

/** GET /api/geo/me, auto-detect country/currency. Auth optional. */
export async function detectGeo(token) {
  try {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const r = await axios.get(`${BACKEND_URL}/api/geo/me`, { headers, timeout: 5000 });
    return r.data; // { country, currency, cached }
  } catch (e) {
    console.info('detectGeo failed (using defaults):', e?.message);
    return null;
  }
}

/**
 * POST /api/billing/verify-signature
 *
 * Razorpay returns three fields after a successful subscription payment in
 * the embedded modal: razorpay_payment_id, razorpay_subscription_id,
 * razorpay_signature. We MUST verify the HMAC server-side before treating
 * the payment as legitimate (otherwise an attacker could fake a success
 * event in the browser).
 */
export async function verifyRazorpaySignature(token, payload) {
  if (!token) return { ok: false, error: 'Not signed in.' };
  try {
    const r = await axios.post(
      `${BACKEND_URL}/api/billing/verify-signature`,
      payload,
      { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 },
    );
    return r.data;
  } catch (e) {
    return { ok: false, error: e?.response?.data?.detail || e?.message };
  }
}

// =============================================================================
// Razorpay Checkout SDK helpers (Phase 37.2)
// =============================================================================
//
// We lazy-load `https://checkout.razorpay.com/v1/checkout.js` once on demand.
// Loading it on every app boot would slow our first paint and we only need
// it at the moment a user clicks "Continue to payment".
//
// Singleton promise pattern so two parallel clicks share one SDK fetch.

let _razorpaySdkPromise = null;

export function loadRazorpayCheckout() {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.Razorpay) return Promise.resolve(true);
  if (_razorpaySdkPromise) return _razorpaySdkPromise;
  _razorpaySdkPromise = new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${RAZORPAY_SDK_URL}"]`);
    if (existing) {
      // Another part of the app already started loading it. Wait for it.
      existing.addEventListener('load', () => resolve(!!window.Razorpay));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const s = document.createElement('script');
    s.src = RAZORPAY_SDK_URL;
    s.async = true;
    s.onload = () => resolve(!!window.Razorpay);
    s.onerror = () => {
      _razorpaySdkPromise = null; // allow retry
      resolve(false);
    };
    document.body.appendChild(s);
  });
  return _razorpaySdkPromise;
}

/**
 * Open Razorpay's embedded subscription Checkout modal.
 *
 * @param {object} args
 * @param {string} args.key_id         , Razorpay key_id from `/checkout`
 * @param {string} args.subscription_id, Razorpay sub_xxx id
 * @param {object} args.prefill        , { name, email, contact }
 * @param {object} args.notes          , pass-through notes (currency, plan, …)
 * @param {string} args.currency       , 'INR' | 'USD'
 * @param {function} args.onSuccess    , receives raw Razorpay response
 * @param {function} args.onDismiss    , called when user closes modal without paying
 * @param {function} args.onFailure    , called on Razorpay-reported payment failure
 * @returns {Promise<boolean>}  true if the modal opened, false if SDK failed to load
 */
export async function openRazorpayModal({
  key_id, subscription_id, prefill = {}, notes = {}, currency,
  onSuccess, onDismiss, onFailure,
}) {
  const ok = await loadRazorpayCheckout();
  if (!ok || !window.Razorpay) return false;

  // Brand the modal as 2120, the user never leaves our universe visually.
  const options = {
    key: key_id,
    subscription_id,
    name: '2120',
    description: 'Financial Clarity For Modern Life',
    image: `${window.location.origin}/brand/logo-mark.png`,
    prefill: {
      name: prefill.name || '',
      email: prefill.email || '',
      contact: prefill.contact || '',
    },
    notes,
    theme: {
      // Warm gold accent line, on a calm background. Matches the in-app
      // CTA so the modal feels like an extension of 2120, not a hop into
      // Razorpay's universe.
      color: '#D4A95D',
      backdrop_color: '#0B1916',
    },
    modal: {
      escape: true,
      ondismiss: () => onDismiss?.(),
      confirm_close: true,
    },
    handler: (response) => {
      // Fires inside the modal on payment-authorised. We don't close here  
      // Razorpay closes itself afterwards. The caller is responsible for
      // verifying the signature server-side before trusting the result.
      onSuccess?.(response);
    },
  };
  // Razorpay's SDK calls `currency` on individual orders, not subscriptions
  // (the currency comes from the plan). We pass it via `notes` for logging.
  if (currency) options.notes.currency_hint = currency;

  const rzp = new window.Razorpay(options);
  rzp.on('payment.failed', (resp) => {
    onFailure?.(resp?.error || { description: 'Payment failed.' });
  });
  rzp.open();
  return true;
}
